import { getViewPort } from "@/components/MapContainer/viewport"
import type { ViewStateProps } from "@wayz/react-gl/lib/gl"
import type { AuthStratVO, AreaItemVO } from "@/api"
import { IndustryCategory, SceneCustomerBusinessTag, SceneCustomerType } from "@/api"
import {
  analysisStatus,
  authMenuList,
  businessStatus,
  businessTypes,
  capitalDateRange,
  capitalRange,
  financingRounds,
  flowFilterTypes,
  flowFilterUnits,
  government,
  housePrice,
  poiCategoryTypes,
  poiEcoTypes,
  sceneTypeTabs,
  taxation,
  branchPositions,
  branchTypes,
  indicatorMenus,
  emptyIconNames,
  provincesReplace,
  headerTabs,
  parkHeaderTabs,
  feijiteHeaderTabs,
} from "../constants/constant"
import cookies from "@/util/cookies"
import { SystemAuthKey } from "@/constants/config"
import { isNH } from "@/constants/NHConfig"
import { getTreeItem, isNumber } from "@/util/util"
// ==============================================
// 获取银行图标
export function getBankIcon(bankName: string) {
  let icon
  if (isNH) {
    icon = require("@/assets/page-home/default.png")
  } else {
    switch (bankName) {
      case "中国农业银行":
        icon = require("@/assets/page-home/bank_ny.png")
        break
      case "中国工商银行":
        icon = require("@/assets/page-home/bank_gs.png")
        break
      case "中国建设银行":
        icon = require("@/assets/page-home/bank_js.png")
        break
      case "中国银行":
        icon = require("@/assets/page-home/bank_zg.png")
        break
      default:
        icon = require("@/assets/page-home/default.png")
        break
    }
  }

  return icon
}

// 获取品牌图标
export function getBrandIcon(brandType: string) {
  const context = require.context("@/assets/brands/", false, /\.(png|jpg)$/)
  const defaultNames = Object.keys(emptyIconNames)
  // 当前品牌
  const name = defaultNames.includes(brandType) ? emptyIconNames[brandType] : "other"
  const url = context(`./${name}.png`)

  return url
}

// 根据path获取key
export function getMenuKeyByPath(path?: string) {
  if (!path) return ""
  const tabs = [...headerTabs, ...parkHeaderTabs, ...feijiteHeaderTabs]

  for (const item of tabs) {
    if (path === item.path) {
      return item.value
    }
  }

  return ""
}

// 根据key获取path
export function getPathByMenuKey(menuKey?: string) {
  if (!menuKey) return ""
  const tabs = [...headerTabs, ...parkHeaderTabs, ...feijiteHeaderTabs]

  for (const item of tabs) {
    if (menuKey === item.value) {
      return item.path
    }
  }

  return ""
}

// 按照凭据排序
export function sorterByTarget(list: any[], targetList: any[]) {
  if (!list || list?.length < 2) {
    return list
  } else {
    const otherList: any[] = []
    const map = new Map()
    for (const value of list) {
      const index = targetList.indexOf(value)
      if (index === -1) {
        otherList.push(value)
      } else {
        map.set(index, value)
      }
    }
    const sortList =
      Array.from(map)
        ?.sort((a, b) => a[0] - b[0])
        ?.map((arr) => arr[1]) || []
    return [...sortList, ...otherList]
  }
}

// 排序moduleTypes
export function sorterModuleTypes(list: string[]) {
  const sorted: string[] = headerTabs.map((tab) => tab.value)
  // 按照凭据排序
  const result = sorterByTarget(list, sorted)
  return result
}

// 排序sceneCustomerTypes
export function sorterCustomerTypes(list: string[]) {
  const sorted = sceneTypeTabs.map((item) => item.value)
  // 按照凭据排序
  const result = sorterByTarget(list, sorted)
  return result
}

// 判断是否是常驻（小区，厂区，公司使用常驻数据）
export function getIsPermanent(type?: string) {
  const keys = ["company", "community", "factory"]
  const values1 =
    Object.keys(poiCategoryTypes)
      .map((v) => (keys.indexOf(v) >= 0 ? poiCategoryTypes[v] : null))
      ?.filter((v) => !!v) || []
  const values2 =
    Object.keys(poiEcoTypes)
      .map((v) => (keys.indexOf(v) >= 0 ? poiEcoTypes[v] : null))
      ?.filter((v) => !!v) || []
  const arr = [...values1, ...values2]
  return arr.indexOf(type) >= 0
}

// 判断是否有场景拓客某个模块的权限
export function isHasSceneTypeAuth(auth: string, auths: string[], singleAuth?: any) {
  if (typeof singleAuth !== "boolean") return false

  if (auth === SceneCustomerType.RURAL_AREA) {
    return auths.includes(auth) && !singleAuth
  } else {
    return auths.includes(auth)
  }
}

// 获取主题类型（localStorage更改不触发页面更新）
export function getTheme() {
  return window.localStorage.getItem("theme") || "dark"
}

// 获取高德地图主题颜色style
export function getMapTheme(theme: string) {
  if (theme === "light") {
    return "amap://styles/whitesmoke"
  } else {
    return "amap://styles/dark"
  }
}

// 获取网点主题颜色
export function getBranchThemeColors() {
  const docEle = document.documentElement
  const computedStyle = getComputedStyle(docEle)
  // 获取 --color CSS 变量值
  // 已开通网点
  const unlockedColor = computedStyle.getPropertyValue("--branch-unlocked")
  // 未开通网点
  const lockedColor = computedStyle.getPropertyValue("--branch-locked")
  // 边框颜色
  const strokeColor = computedStyle.getPropertyValue("--map-tooltip-stroke-color")
  // 填充背景色
  const fillColor = computedStyle.getPropertyValue("--map-tooltip-fill-color")
  // 字体颜色 text
  const textColor = computedStyle.getPropertyValue("--map-tooltip-text-color")
  const textColor2 = computedStyle.getPropertyValue("--map-tooltip-text2-color")
  // icon 文本样式
  const iconStrokeColor = computedStyle.getPropertyValue("--map-icon-stroke-color")
  const iconTextColor = computedStyle.getPropertyValue("--map-icon-text-color")
  const iconTextBackgroundColor = computedStyle.getPropertyValue("--map-icon-text-background-color")

  const iconTextColorHover = computedStyle.getPropertyValue("--map-icon-text-color-hover")
  const iconTextBackgroundColorHover = computedStyle.getPropertyValue(
    "--map-icon-text-background-color-hover",
  )
  // 智能选址网点
  const selectionColor = getComputedStyle(docEle).getPropertyValue("--branch-selection")

  const colorTheme = {
    unlockedColor: unlockedColor,
    lockedColor: lockedColor,
    strokeColor,
    fillColor: fillColor,
    textColor,
    textColor2,
    iconStrokeColor,
    iconTextColor,
    iconTextBackgroundColor,
    iconTextColorHover,
    iconTextBackgroundColorHover,
    selectionColor: selectionColor,
  }
  return colorTheme
}

// 计算table的rowHeight
export function getTableRowHeight(row = 1) {
  const p = document.createElement("p")
  p.className = "name-container flex-column"
  for (let i = 0; i < row; i++) {
    const span = document.createElement("span")
    span.textContent = "文本内容"
    span.className = "ellipsis"
    p.appendChild(span)
  }
  if (window.getComputedStyle) {
    document.body.appendChild(p)
    const rowHeight = parseFloat(window.getComputedStyle(p).height)
    document.body.removeChild(p)
    return rowHeight
  } else {
    return null
  }
}

// 获取header中文本的宽度
export function getHeaderContentWidth(txt?: string) {
  if (!txt || !window.getComputedStyle) {
    return null
  } else {
    const root = document.getElementById("root")
    const div = document.createElement("div")
    div.classList.add("text-2xl")
    div.classList.add("pangmen")
    div.style.display = "inline-block"
    div.textContent = txt
    root?.appendChild(div)
    const width = parseFloat(window.getComputedStyle(div).width)
    root?.removeChild(div)
    return width
  }
}

// 获取菜单名称
export function getAuthMenuName(menuKey: string) {
  for (const item of authMenuList) {
    const { value, label } = item
    if (value === menuKey) {
      return label
    }
  }
  return ""
}

// 获取权限策略
export function getAuthStrategy(values: string[] = [], auths: AuthStratVO[] = []) {
  let names: string[] = []
  for (const v of values) {
    for (const item of auths) {
      const { value, name } = item
      if (value === v) {
        names.push(name)
        break
      }
    }
  }
  if (auths?.length && names?.length === auths?.length) {
    names = ["全部权限"]
  }
  return names
}

// 兼容H5输入框focus时底部出现空白
export function compatibleFocusBlank() {
  const u = navigator.userAgent
  // app = navigator.appVersion
  const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  if (isIOS) {
    setTimeout(() => {
      const scrollHeight = document.documentElement.scrollTop || document.body.scrollTop || 0
      window.scrollTo(0, Math.max(scrollHeight - 1, 0))
    }, 200)
  }
}

// 根据order获取isAsc
export function getIsAscByOrder(order?: string) {
  if (order === "descend") {
    return false
  } else if (order === "ascend") {
    return true
  } else {
    return null
  }
}

// 过滤tree数据：将users放入children中
export function pipeTreeData(tree: any[]) {
  for (let i = 0; i < tree?.length; i++) {
    const item = tree[i]
    item.type = "org"
    if (item?.children) {
      pipeTreeData(item.children)
      if (item?.users?.length) {
        item.users?.map((user: any) => (user.type = "user") && user)
        item.children.unshift(...item.users)
      }
    } else {
      item.users?.map((user: any) => (user.type = "user") && user)
      item.children = item?.users?.length ? item.users : []
    }
    delete item.users
  }
  return tree
}

// 获取商户状态
export function getBusinessStatus(status: any) {
  for (const item of businessStatus) {
    const { value, text } = item
    if (value === status) {
      return text
    }
  }
  return "- -"
}

// 获取商户合作方式
export function getBusinessMode(cooMode: any) {
  for (const item of businessTypes) {
    const { value, label } = item
    if (value === cooMode) {
      return label
    }
  }
  return "- -"
}

// 获取商户类型
export function getBusinessType(businessType: any) {
  let res
  switch (businessType) {
    case SceneCustomerBusinessTag.RESTAURANT:
      res = "餐饮"
      break
    case SceneCustomerBusinessTag.SHOPPING:
      res = "购物"
      break
    default:
      res = "- -"
      break
  }
  return res
}

// 智能选址-状态
export function getAnalysisStatus(status: string) {
  for (const item of analysisStatus.slice(1)) {
    const { value, label } = item
    if (value === status) {
      return label
    }
  }
  return "- -"
}

// 时间区间组件自定义校验
export function dateRangeVerify(text = "时间", value: number, callback: any) {
  const reg = /^\d{4}$/
  if (value < 1900) {
    callback && callback(`${text}最早不得早于1900年`)
    return false
  }
  if (!reg.test(String(value))) {
    callback && callback(`${text}自定义年份应为4位数值`)
    return false
  }
  return true
}

// 筛选条件组件设置data
export function filterGetData(key: string, data: any) {
  return (prev: any) => {
    if (data instanceof Map) {
      const params = {}
      const keys = Array.from(data.keys())
      for (const dataKey of keys) {
        const values = data.get(dataKey)
        const oldValue = prev[dataKey]
        params[dataKey] = {
          ...oldValue,
          ...values,
        }
      }
      return params
    } else {
      return {
        [key]: {
          ...prev[key],
          ...data,
        },
      }
    }
  }
}

// 客流类型
export function getFlowFilterType(type: string) {
  for (const item of flowFilterTypes) {
    const { id, name } = item
    if (id === type) {
      return name
    }
  }
  return ""
}

// 客流单位
export function getFlowFilterUnit(unit: string) {
  for (const item of flowFilterUnits) {
    const { id, name } = item
    if (id === unit) {
      return name
    }
  }
  return ""
}

// 房屋均价
export function getHousePrice(price: string) {
  for (const item of housePrice) {
    const { value, label } = item
    if (value === price) {
      return label
    }
  }
  return ""
}

// 注册资本
export function getCapitalRange(capital: string) {
  for (const item of capitalRange) {
    const { value, label } = item
    if (value === capital) {
      return label
    }
  }
  return ""
}

// 注册时间
export function getCapitalDateRange(date: string) {
  for (const item of capitalDateRange) {
    const { value, label } = item
    if (value === date) {
      return label
    }
  }
  return ""
}

// 政府机构
export function getGovernment(key: string) {
  for (const item of government) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 工商税务机构
export function getTaxation(key: string) {
  for (const item of taxation) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 融资轮次
export function getFinancingRounds(key: string) {
  for (const item of financingRounds) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 获取场景类型
export function getSceneType(key: string) {
  for (const item of sceneTypeTabs) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 获取当前页面path
export function getCurrentPageType(pathname?: string) {
  const path = pathname || window.location.href
  const reg = /[^\/]+(?!.*\/)/g

  return path.match(reg)?.[0] ?? "analysis"
}

// 获取品牌类型
export function getIndustryCategory(type?: string) {
  if (type === IndustryCategory.BANK) {
    return "网点"
  } else if (type === IndustryCategory.ADVERTISEMENT) {
    return "点位"
  } else {
    return "门店"
  }
}

// 获取rem的基准值
export function getRemStatic() {
  return parseFloat(document.documentElement.style.fontSize) || 0
}

// 获取可视范围宽高
export function getClientSize() {
  return {
    clientWidth: document.documentElement.clientWidth,
    clientHeight: document.documentElement.clientHeight,
  }
}

// 该zoom下，center与leftCenter的偏移量
export function getCenterOffset(
  getViewState: () => ViewStateProps | ViewStateProps,
  zoom?: number | ((viewState?: any) => any),
) {
  // 获取可视范围宽高
  const { clientWidth, clientHeight } = getClientSize()
  // 右侧内容width：66.5rem
  const rightWidth = getRemStatic() * 66.5
  // 视图
  const viewState = typeof getViewState === "function" ? getViewState() : getViewState
  // 地图中心点
  const { longitude = 0, latitude = 0 } = viewState
  const myState =
    typeof zoom === "number"
      ? {
          ...viewState,
          zoom,
        }
      : typeof zoom == "function"
      ? {
          ...viewState,
          ...zoom(viewState),
        }
      : viewState
  const viewPort = getViewPort(myState)
  // 左侧中心点
  const [lng = 0, lat = 0] = viewPort?.unproject([
    Math.round((clientWidth - rightWidth) / 2),
    Math.round(clientHeight / 2),
  ]) || [0, 0]

  return {
    center: [longitude, latitude],
    leftCenter: [lng, lat],
    subtract: [Math.abs(longitude - lng), Math.abs(latitude - lat)],
  }
}

// 网点类型
export function getBranchType(key?: string) {
  if (!key) return ""
  for (const item of branchTypes) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 网点定位
export function getBranchPositions(key?: string) {
  if (!key) return ""
  for (const item of branchPositions) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 经营情况菜单
export function getIndicatorMenu(key?: string) {
  if (!key) return ""
  for (const item of indicatorMenus) {
    const { value, label } = item
    if (value === key) {
      return label
    }
  }
  return ""
}

// 手机号脱敏
export function encryptPhone(phone = "") {
  if (!phone) return
  const reg = /(\d{3})\d{4}(\d{4})/
  return phone?.replace(reg, "$1****$2")
}

// 身份证号脱敏
export function encryptIdcard(idcard = "") {
  if (!idcard) return
  const reg = /^(.{6}).+(.{3})$/
  return idcard?.replace(reg, "$1****$2")
}

// 将城市code转换为省份code
export function getProvinceCodeByCity(cityCode: string | number) {
  return Number(`${String(cityCode).slice(0, 2)}0000`)
}

// 判断是否为区县code
export function isDistrictCode(code?: string) {
  if (!code) return false
  return !/00$/.test(code)
}

// 根据id判断省市区级别（全国0，省1，市2，区3）
export function getLevelByCityCode(code?: string) {
  if (!code) return undefined
  const _code = `${code}`
  let levelCode = 3
  let levelName = "DISTRICT"
  if (_code === "100000") {
    levelCode = 0
    levelName = "CN"
  } else if (/0000$/.test(_code)) {
    levelCode = 1
    levelName = "PROVINCES"
  } else if (/00$/.test(_code)) {
    levelCode = 2
    levelName = "CITY"
  }
  return {
    levelCode,
    levelName,
  }
}

// 是否显示系统配置
export function isShowConfig() {
  const systemConfigAuth = cookies.getItem(SystemAuthKey)

  return systemConfigAuth ? systemConfigAuth === "1" : false
}

// 获取当前区域范围
export function getCurrentAreaConfig(cityId: string, areaId: string, configs: AreaItemVO[]) {
  if (cityId) {
    // 省市置换
    const replaceCode = provincesReplace[Number(cityId)]

    return replaceCode
      ? getTreeItem(configs, replaceCode, areaId)
      : getTreeItem(configs, Number(cityId), areaId)
  } else {
    return null
  }
}

// 计算推荐指数，参数[0,1]
export function getRecommendValue(score: number) {
  // round(recommendScore * 5, 1)
  if (!isNumber(score)) return 0
  if (score <= 0) return 0
  if (score >= 1) return 5
  const values = [
    // range包前不包后
    { star: 0.5, range: [0, 0.2] },
    { star: 1, range: [0.2, 0.3] },
    { star: 1.5, range: [0.3, 0.4] },
    { star: 2, range: [0.4, 0.5] },
    { star: 2.5, range: [0.5, 0.6] },
    { star: 3, range: [0.6, 0.7] },
    { star: 3.5, range: [0.7, 0.8] },
    { star: 4, range: [0.8, 0.9] },
    { star: 4.5, range: [0.9, 1] },
  ]
  for (let i = 0; i < values.length; i++) {
    const { star, range } = values[i]
    const [range1, range2] = range
    if (score >= range1 && score < range2) {
      return star
    }
  }
  return 0
}
